/**
 * CurrencyInput Component
 * Input field with Indian Rupee prefix and number formatting
 */

import { forwardRef, type InputHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

interface CurrencyInputProps
	extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "onChange"> {
	value: string | number | undefined;
	onChange: (value: string) => void;
	error?: string;
	label?: string;
}

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
	({ value, onChange, error, label, className, id, ...props }, ref) => {
		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const raw = e.target.value.replace(/[^0-9]/g, "");
			onChange(raw);
		};

		const formatDisplay = (val: string | number | undefined): string => {
			if (val === undefined || val === "") return "";
			const num = typeof val === "string" ? parseInt(val, 10) : val;
			if (isNaN(num)) return "";
			return num.toLocaleString("en-IN");
		};

		return (
			<div className="space-y-1">
				{label && (
					<label
						htmlFor={id}
						className="block text-sm font-medium text-foreground"
					>
						{label}
					</label>
				)}
				<div className="relative">
					<span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-muted-foreground">
						₹
					</span>
					<input
						ref={ref}
						id={id}
						type="text"
						inputMode="numeric"
						value={formatDisplay(value)}
						onChange={handleChange}
						className={cn(
							"flex h-10 w-full rounded-md border border-input bg-background pl-7 pr-3 py-2 text-sm",
							"ring-offset-background placeholder:text-muted-foreground",
							"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
							"disabled:cursor-not-allowed disabled:opacity-50",
							error && "border-destructive",
							className,
						)}
						{...props}
					/>
				</div>
				{error && <p className="text-xs text-destructive">{error}</p>}
			</div>
		);
	},
);

CurrencyInput.displayName = "CurrencyInput";
